/**
 * Arousal Mechanic Detail Page
 * 
 * Displays full details for a single arousal mechanism including:
 * - Name, category, timeframe badges
 * - Key stats (effort, trainability, intensity impact, risk, evidence)
 * - Description and page sections
 * - Activation/inhibition cues
 * - Linked kinks, sex actions, gear
 * - Related mechanisms (edges)
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft, Brain, Clock, Zap, Target, Shield, BookOpen,
    ChevronRight, Users, Heart, Sparkles, AlertTriangle,
    TrendingUp, TrendingDown, ArrowRight
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Category colors
const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
        case 'physiological': return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
        case 'psychological': return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
        case 'relational': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
        case 'environmental': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
        default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
};

// Timeframe badges
const getTimeframeBadge = (timeframe) => {
    const colors = {
        immediate: 'text-amber-400 bg-amber-500/10',
        short: 'text-yellow-400 bg-yellow-500/10',
        medium: 'text-lime-400 bg-lime-500/10',
        long: 'text-teal-400 bg-teal-500/10',
        lifetime: 'text-cyan-400 bg-cyan-500/10'
    };
    return colors[timeframe?.toLowerCase()] || 'text-gray-400 bg-gray-500/10';
};

// Stat bar component
const StatBar = ({ label, value, max = 10, color = 'wine', icon: Icon }) => {
    const percentage = Math.min(100, (value / max) * 100);
    return (
        <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-cream/70 flex items-center gap-2">
                    {Icon && <Icon size={14} className="text-cream/50" />}
                    {label}
                </span>
                <span className="text-sm font-medium text-cream/90">{value}/{max}</span>
            </div>
            <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full bg-${color}-500 transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
};

// Page section component
const PageSection = ({ title, content, icon: Icon }) => {
    if (!content) return null;
    return (
        <section className="mb-6 p-5 rounded-xl bg-[#0A0A0A]/60 border border-wine-800/20">
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon className="w-5 h-5 text-[#C4A35A]" />}
                <h3 className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">{title}</h3>
            </div>
            <div className="prose prose-invert max-w-none">
                <p className="text-cream/70 leading-relaxed whitespace-pre-wrap">{content}</p>
            </div>
        </section>
    );
};

// Cue pills component
const CuePills = ({ title, cues, color = 'emerald', icon: Icon }) => {
    if (!cues || cues.length === 0) return null;
    return (
        <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon className={`w-4 h-4 text-${color}-400`} />}
                <h3 className="text-sm font-medium text-cream/70 uppercase tracking-wider">{title}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
                {cues.map((cue, idx) => (
                    <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-sm bg-${color}-500/10 text-${color}-400 border border-${color}-500/20`}
                    >
                        {cue}
                    </span>
                ))}
            </div>
        </section>
    );
};

// Related entity link component
const EntityLink = ({ to, name, category, badge }) => (
    <Link
        to={to}
        className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
    >
        <div>
            <span className="text-white group-hover:text-wine-300 transition-colors">{name}</span>
            {category && <span className="ml-2 text-sm text-dark-500">{category}</span>}
            {badge && <span className="ml-2 px-2 py-0.5 text-xs rounded bg-wine-900/50 text-wine-300">{badge}</span>}
        </div>
        <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
    </Link>
);

// Relationship edge component
const RelationshipEdge = ({ mechanism, relationshipType, isOutgoing }) => {
    const typeLabels = {
        reinforces: { label: 'Reinforces', color: 'emerald', icon: TrendingUp },
        counteracts: { label: 'Counteracts', color: 'rose', icon: TrendingDown },
        requires: { label: 'Requires', color: 'amber', icon: Target },
        builds_to: { label: 'Builds To', color: 'blue', icon: ArrowRight },
        gateway_to: { label: 'Gateway To', color: 'purple', icon: Sparkles }
    };

    const typeInfo = typeLabels[relationshipType] || { label: relationshipType, color: 'gray', icon: ArrowRight };
    const TypeIcon = typeInfo.icon;

    return (
        <Link
            to={`/mechanic/${mechanism.id}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
        >
            <div className={`p-2 rounded-lg bg-${typeInfo.color}-500/10`}>
                <TypeIcon size={16} className={`text-${typeInfo.color}-400`} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded bg-${typeInfo.color}-500/10 text-${typeInfo.color}-400`}>
                        {isOutgoing ? '→' : '←'} {typeInfo.label}
                    </span>
                </div>
                <span className="text-white group-hover:text-wine-300 transition-colors block truncate">
                    {mechanism.name}
                </span>
                {mechanism.summary && (
                    <span className="text-sm text-dark-500 block truncate">{mechanism.summary}</span>
                )}
            </div>
            <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400 flex-shrink-0" />
        </Link>
    );
};

const ArousalMechanicDetailPage = () => {
    const { id } = useParams();
    const [mechanism, setMechanism] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchMechanism();
    }, [id]);

    const fetchMechanism = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getArousalMechanic(id);
            setMechanism(data);
        } catch (err) {
            console.error('Error fetching arousal mechanic:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading mechanism...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !mechanism) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">🧠</div>
                    <h2 className="text-xl font-bold text-white mb-2">Mechanism Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This arousal mechanism does not exist.'}</p>
                    <Link to="/mechanics" className="btn-primary">
                        Back to Mechanics
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = mechanism.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    const categoryClass = getCategoryColor(mechanism.category);
    const timeframeClass = getTimeframeBadge(mechanism.timeframe);
    const showMechanicEdges = ((mechanism.outgoingEdges?.length || 0) + (mechanism.incomingEdges?.length || 0)) >= 4;
    const showRelatedKinks = (mechanism.kinks?.length || 0) >= 4;
    const showRelatedActions = (mechanism.sexActions?.length || 0) >= 4;
    const showRelatedGear = (mechanism.gear?.length || 0) >= 4;

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/mechanics"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Mechanisms</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {mechanism.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/mechanic/${mechanism.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {mechanism.parentVariant.name}
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-wine-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-wine-700/20 flex items-center justify-center">
                                <Brain size={28} className="text-wine-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {mechanism.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryClass}`}>
                                        {mechanism.category}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${timeframeClass}`}>
                                        <Clock size={12} className="inline mr-1" />
                                        {mechanism.timeframe}
                                    </span>
                                    {mechanism.requiresPartner && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                            <Users size={12} className="inline mr-1" />
                                            Requires Partner
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Summary */}
                        {mechanism.summary && (
                            <p className="text-cream/70 text-lg leading-relaxed mt-4">
                                {mechanism.summary}
                            </p>
                        )}

                        {/* Gold Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Zap size={18} className="mx-auto mb-1 text-amber-400" />
                                <div className="text-lg font-bold text-cream">{mechanism.effortLevel || 5}</div>
                                <div className="text-xs text-dark-400">Effort</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Target size={18} className="mx-auto mb-1 text-emerald-400" />
                                <div className="text-lg font-bold text-cream">{mechanism.trainability || 5}</div>
                                <div className="text-xs text-dark-400">Trainability</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <TrendingUp size={18} className="mx-auto mb-1 text-pink-400" />
                                <div className="text-lg font-bold text-cream">{mechanism.intensityImpact > 0 ? '+' : ''}{mechanism.intensityImpact || 0}</div>
                                <div className="text-xs text-dark-400">Intensity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Shield size={18} className="mx-auto mb-1 text-rose-400" />
                                <div className="text-lg font-bold text-cream">{mechanism.riskLevel || 3}</div>
                                <div className="text-xs text-dark-400">Risk</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <BookOpen size={18} className="mx-auto mb-1 text-blue-400" />
                                <div className="text-lg font-bold text-cream">{mechanism.evidenceBacking || 5}</div>
                                <div className="text-xs text-dark-400">Evidence</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Description */}
                {mechanism.description && (
                    <section className="mb-8">
                        <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-3 tracking-wide">Description</h2>
                        <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{mechanism.description}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Activation Cues */}
                <CuePills
                    title="Activation Cues"
                    cues={mechanism.activationCues}
                    color="emerald"
                    icon={TrendingUp}
                />

                {/* Inhibition Cues */}
                <CuePills
                    title="Inhibition Cues"
                    cues={mechanism.inhibitionCues}
                    color="rose"
                    icon={TrendingDown}
                />

                {/* Body/Hormone Info */}
                {(mechanism.bodyApplicability !== 'universal' || mechanism.hormoneRelevance !== 'all') && (
                    <section className="mb-6 p-5 rounded-xl bg-purple-500/5 border border-purple-500/20">
                        <h3 className="text-lg font-semibold text-white mb-3">Applicability</h3>
                        <div className="flex flex-wrap gap-4">
                            {mechanism.bodyApplicability && mechanism.bodyApplicability !== 'universal' && (
                                <div>
                                    <span className="text-sm text-dark-400">Body Type:</span>
                                    <span className="ml-2 text-purple-400">{mechanism.bodyApplicability}</span>
                                </div>
                            )}
                            {mechanism.hormoneRelevance && mechanism.hormoneRelevance !== 'all' && (
                                <div>
                                    <span className="text-sm text-dark-400">Hormone Context:</span>
                                    <span className="ml-2 text-purple-400">{mechanism.hormoneRelevance}</span>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* Page Sections */}
                <PageSection
                    title="Deep Dive"
                    content={getSectionContent('deep_dive')}
                    icon={Brain}
                />
                <PageSection
                    title="Common Triggers"
                    content={getSectionContent('common_triggers')}
                    icon={Sparkles}
                />
                <PageSection
                    title="Enhancement Tips"
                    content={getSectionContent('enhancement_tips')}
                    icon={Heart}
                />
                <PageSection
                    title="Scientific Basis"
                    content={getSectionContent('scientific_basis')}
                    icon={BookOpen}
                />

                {/* Sources */}
                {mechanism.sources && mechanism.sources.length > 0 && (
                    <section className="mb-8 p-5 rounded-xl bg-dark-900/50 border border-dark-800">
                        <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                            <BookOpen size={18} className="text-[#C4A35A]" />
                            Sources
                        </h3>
                        <ul className="space-y-2">
                            {mechanism.sources.map((source, idx) => (
                                <li key={idx} className="text-sm text-dark-400 pl-4 border-l-2 border-dark-700">
                                    {source}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Related Mechanisms (Edges) */}
                {showMechanicEdges && (
                        <section className="mb-8">
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">
                                Related Mechanisms
                            </h2>
                            <div className="space-y-2">
                                {mechanism.outgoingEdges?.map(edge => (
                                    <RelationshipEdge
                                        key={`out-${edge.id}`}
                                        mechanism={edge}
                                        relationshipType={edge.ArousalMechanicEdge?.relationshipType}
                                        isOutgoing={true}
                                    />
                                ))}
                                {mechanism.incomingEdges?.map(edge => (
                                    <RelationshipEdge
                                        key={`in-${edge.id}`}
                                        mechanism={edge}
                                        relationshipType={edge.ArousalMechanicEdge?.relationshipType}
                                        isOutgoing={false}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                {/* Related Kinks */}
                {showRelatedKinks && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Related Kinks</h2>
                        <div className="space-y-2">
                            {mechanism.kinks.map(kink => (
                                <EntityLink
                                    key={kink.id}
                                    to={`/kink/${kink.id}`}
                                    name={kink.name}
                                    category={kink.category}
                                    badge={kink.ArousalMechanicKink?.relevance}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Sex Actions */}
                {showRelatedActions && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Related Actions</h2>
                        <div className="space-y-2">
                            {mechanism.sexActions.map(action => (
                                <EntityLink
                                    key={action.id}
                                    to={`/action/${action.id}`}
                                    name={action.name}
                                    category={action.category}
                                    badge={action.ArousalMechanicSexAction?.relevance}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Gear */}
                {showRelatedGear && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Related Gear</h2>
                        <div className="space-y-2">
                            {mechanism.gear.map(item => (
                                <EntityLink
                                    key={item.id}
                                    to={`/gear/${item.id}`}
                                    name={item.name}
                                    category={item.category}
                                    badge={item.ArousalMechanicGear?.usage}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* Sub-Variants */}
                {mechanism.childVariants && mechanism.childVariants.length > 0 && (
                    <section className="mb-8">
                        <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide flex items-center gap-2">
                            <Brain size={20} className="text-wine-400" />
                            Variants of {mechanism.name}
                        </h2>
                        <div className="space-y-2">
                            {mechanism.childVariants.map(variant => (
                                <EntityLink
                                    key={variant.id}
                                    to={`/mechanic/${variant.id}`}
                                    name={variant.name}
                                    category={variant.category}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ArousalMechanicDetailPage;
